import { forwardRef, useRef, type ComponentProps } from 'react';
import { NumericFormat, type NumberFormatValues, type OnValueChange } from 'react-number-format';
import { BaseInput } from '@shared/widgets';

const MAX_FRACTION_DIGITS = 2;
const MAX_SAFE_AMOUNT = Math.floor(Number.MAX_SAFE_INTEGER / 10 ** MAX_FRACTION_DIGITS);

const isAmountValueAllowed = ({ floatValue }: NumberFormatValues) => floatValue === undefined || Math.abs(floatValue) <= MAX_SAFE_AMOUNT;

type BaseInputProps = ComponentProps<typeof BaseInput>;

type AmountInputProps = Omit<BaseInputProps, 'onChange' | 'type' | 'value' | 'onlyPositive'> & {
  value?: number | string | null;
  onChange?: (value: number | undefined) => void;
  onValueChange?: OnValueChange;
  decimalScale?: number;
  allowNegative?: boolean;
};

const NumericBaseInput = forwardRef<
  HTMLInputElement,
  BaseInputProps & { onChange?: (e: { target: HTMLInputElement; currentTarget: HTMLInputElement }) => void }
>(({ onChange, ...props }, ref) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <BaseInput
      {...props}
      ref={(node) => {
        inputRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      type='text'
      onChange={() => {
        const el = inputRef.current;
        if (!el || !onChange) return;
        onChange({ target: el, currentTarget: el });
      }}
    />
  );
});
NumericBaseInput.displayName = 'NumericBaseInput';

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  ({ value, onChange, onValueChange, decimalScale = MAX_FRACTION_DIGITS, allowNegative = false, ...props }, ref) => (
    <NumericFormat
      {...props}
      getInputRef={ref}
      customInput={NumericBaseInput}
      value={value ?? ''}
      allowNegative={allowNegative}
      onlyPositive={!allowNegative}
      decimalScale={decimalScale}
      isAllowed={isAmountValueAllowed}
      onValueChange={(values, sourceInfo) => {
        onValueChange?.(values, sourceInfo);
        onChange?.(values.floatValue);
      }}
    />
  ),
);
AmountInput.displayName = 'AmountInput';
